import React, { useMemo, useState } from 'react';
import { Box, Button, Group, Text, Tooltip } from '@mantine/core';
import type { ScimEntityService } from '../../services/ScimEntityService';
import type { EntityEntitlementForTargetResponse } from '../../types/EntityEntitlements';
import { getAttributeDisplayNameForPropertyPath } from '../../types/EntityEntitlements';
import { EntityBrowserDialog } from '../dialogs';

export interface PaginatedEntityViewerProps {
    /** Parent entity type (e.g. Group). */
    entityType: string;
    /** Parent entity id. */
    entityId: string;
    /** Attribute id/name (e.g. "$.MemberTransitive" or "MemberTransitive"). */
    attributeId: string;
    /** Label to show in the viewer title. */
    attributeLabel: string;
    /** Tier3 entitlement response for computing request displayName. */
    targetEntitlement: EntityEntitlementForTargetResponse | null | undefined;
    /** Service used to call getEntityPropertyPage. */
    entityService: ScimEntityService;
    /** Tier3-reported Count (optional). */
    count?: number | null;
    /** Tier3-reported Length (optional). */
    length?: number | null;
}

export const PaginatedEntityViewer: React.FC<PaginatedEntityViewerProps> = ({
    entityType,
    entityId,
    attributeId,
    attributeLabel,
    targetEntitlement,
    entityService,
    count,
    length
}) => {
    const [open, setOpen] = useState(false);

    const countStr = typeof count === 'number' ? count.toLocaleString() : null;
    const lengthStr = typeof length === 'number' ? length.toLocaleString() : null;

    const title = useMemo(() => `View ${attributeLabel}`, [attributeLabel]);

    return (
        <>
            <Group gap="sm" align="center" wrap="wrap">
                <Tooltip label="Large reference list (not loaded)">
                    <Text size="sm" c="dimmed">Not loaded*</Text>
                </Tooltip>
                {(countStr || lengthStr) && (
                    <Text size="sm" c="dimmed">
                        {countStr ? `Count: ${countStr}` : ''}
                        {countStr && lengthStr ? ' · ' : ''}
                        {lengthStr ? `Length: ${lengthStr}` : ''}
                    </Text>
                )}
                <Button size="xs" variant="outline" onClick={() => setOpen(true)}>
                    View
                </Button>
            </Group>

            <EntityBrowserDialog
                open={open}
                onClose={() => setOpen(false)}
                entityType="Entity"
                title={title}
                callerName="PaginatedEntityViewer"
                viewOnly
                disableSearch
                disableSort
                fetchPageOverride={async ({ page, pageSize }) => {
                    const propertyDisplayName = getAttributeDisplayNameForPropertyPath(targetEntitlement ?? null, attributeId);
                    const res = await entityService.getEntityPropertyPage(entityType, entityId, propertyDisplayName, page, pageSize);
                    return { data: res.data ?? [], totalPages: res.totalPages ?? 1, totalResults: res.totalResults ?? 0 };
                }}
            />
        </>
    );
};

